"use client";

import React, { useState, useEffect } from "react";
import { MapContainer, GeoJSON, TileLayer } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    IconButton
} from "@mui/material";
import { Public, LocationOn, Close } from '@mui/icons-material';

const geoUrl = "https://raw.githubusercontent.com/datasets/geo-countries/main/data/countries.geojson";

const impactData = {
    USA: { name: "United States", regions: ["Essex", "Mass Health", "MiddleSex", "Norfolk", "Suffolk", "Worcester"] },
    KEN: { name: "Kenya", regions: ["Kakamega", "Nakuru", "Nairobi", "Kajiado"] },
    SSD: { name: "South Sudan", regions: ["Impact in various regions"] },
    GHA: { name: "Ghana", regions: ["Accra"] },
    MWI: { name: "Malawi", regions: ["Mangochi"] },
};

const highlightedISOCodes = Object.keys(impactData);

const InteractiveMap = () => {
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [geoData, setGeoData] = useState(null);

    useEffect(() => {
        fetch(geoUrl)
            .then(res => res.json())
            .then(data => {
                setGeoData(data);
            })
            .catch(err => console.error("Error fetching geo data:", err));
    }, []);

    const getIsoA3 = (feature) => {
        return feature.properties["ISO3166-1-Alpha-3"] || feature.properties.iso_a3 || feature.properties.ADM0_A3 || feature.properties.ISO_A3 || feature.id;
    };

    const styleFeature = (feature) => {
        const isoCode = getIsoA3(feature);
        const isHighlighted = highlightedISOCodes.includes(isoCode);

        return {
            fillColor: isHighlighted ? "#1976d2" : "#D6D6DA",
            fillOpacity: isHighlighted ? 0.9 : 0.7,
            weight: 1,
            color: "white",
            dashArray: "3",
        };
    };

    const onEachFeature = (feature, layer) => {
        const isoCode = getIsoA3(feature);
        const isHighlighted = highlightedISOCodes.includes(isoCode);

        layer.setStyle(styleFeature(feature));

        if (isHighlighted) {
            layer.on({
                click: () => {
                    setSelectedCountry(impactData[isoCode]);
                },
                mouseover: (e) => {
                    e.target.setStyle({
                        fillColor: "#115293",
                        weight: 2,
                        color: "#666"
                    });
                    e.target.bringToFront();
                },
                mouseout: (e) => {
                    e.target.setStyle(styleFeature(feature));
                }
            });
            layer.options.interactive = true;
        } else {
            layer.on({
                click: () => {
                    setSelectedCountry(null);
                },
                mouseover: (e) => {
                    e.target.setStyle({
                        fillColor: "#BDBDBD"
                    });
                },
                mouseout: (e) => {
                    e.target.setStyle(styleFeature(feature));
                }
            });
            layer.options.interactive = true;
        }
    };

    if (!geoData) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>Loading Map...</Box>;
    }

    return (
        <Box sx={{ position: 'relative', width: '100%', height: '400px', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
            <MapContainer
                center={[0, 0]}
                zoom={1.5}
                scrollWheelZoom={false}
                style={{ height: "100%", width: "100%", zIndex: 1 }}
            >

                <GeoJSON
                    key={JSON.stringify(geoData)}
                    data={geoData}
                    style={styleFeature}
                    onEachFeature={onEachFeature}
                    pane="overlayPane"
                />
            </MapContainer>

            <Card sx={{
                position: 'absolute',
                bottom: 16,
                left: 16,
                zIndex: 401,
                maxWidth: 300,
                display: selectedCountry ? 'block' : 'none',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
            }}>
                <CardHeader
                    avatar={<Public />}
                    title={selectedCountry?.name}
                    subheader="Our Impacted Regions"
                    action={
                        <IconButton aria-label="close" onClick={() => setSelectedCountry(null)}>
                            <Close />
                        </IconButton>
                    }
                />
                <CardContent sx={{ pt: 0 }}>
                    <List dense>
                        {selectedCountry?.regions.map((region) => (
                            <ListItem key={region}>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <LocationOn fontSize="small" color="primary" />
                                </ListItemIcon>
                                <ListItemText primary={region} />
                            </ListItem>
                        ))}
                    </List>
                </CardContent>
            </Card>

            {!selectedCountry && (
                <Chip
                    label="Click a highlighted country to see details"
                    sx={{
                        position: 'absolute',
                        bottom: 16,
                        left: 16,
                        zIndex: 401,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)'
                    }}
                />
            )}
        </Box>
    );
};

export default InteractiveMap;